<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { useBrandsStore } from "@/stores/brands";
import { useBrandSwitcherStore } from "@/stores/brandSwitcher";
import { useUserBrandsStore } from "@/stores/userBrands";

/**
 * Topbar dropdown that scopes every admin API call to the selected
 * brand. Visible to anyone who can read the brand registry; the
 * choice is purely presentational from the GUI's perspective —
 * server-side, it stamps `X-Brand-Code` on every authFetch request
 * so the BrandContextMiddleware on `/api/v1/*` filters accordingly.
 *
 * "Kaikki brändit" (null) is the default and corresponds to admin /
 * cross-brand scope. When the calling user has a non-empty
 * UserBrand allowlist, the dropdown only shows the brands they're
 * allowed into and "Kaikki brändit" is hidden: the API's
 * BrandAccessGuard no longer runs admin scope for restricted users
 * (single-brand users get narrowed server-side, multi-brand users
 * get 403 without X-Brand-Code), so the GUI auto-selects the first
 * permitted brand whenever the persisted choice is empty or not in
 * the allowlist.
 */
const brands = useBrandsStore();
const switcher = useBrandSwitcherStore();
const userBrands = useUserBrandsStore();

/// Käytetään `allActiveBrands`:ta — backend `brands.list(scope)`
/// suodattaa brand-bound-callereille vain heidän brandinsa. Kun
/// käyttäjä on valinnut WEB1:n, normaali brand-fetch näyttäisi vain
/// WEB1:n switcherissä, ja toiseen brändiin pääsisi vain valitsemalla
/// ensin "Kaikki brändit". `fetchAllUnscoped()` ohittaa X-Brand-Code-
/// headerin → palauttaa täyden brand-listan. UserBrand-allowlist
/// suodatetaan tässä GUI-puolella — admin näkee kaikki, restricted-
/// user näkee vain omat.
const isRestricted = computed(() => (userBrands.mine?.length ?? 0) > 0);

const options = computed(() => {
  const allowed = userBrands.mine;
  if (!allowed || allowed.length === 0) return brands.allActiveBrands;
  // Rajattu käyttäjä: brändit tulevat /me/brands-vastauksen mukana
  // (GET /brands vaatisi brändivalinnan jota ollaan vasta tekemässä).
  return allowed
    .map((u) => u.brand)
    .filter((b): b is NonNullable<typeof b> => !!b && b.isActive)
    .map((b) => ({ id: b.id, code: b.code, name: b.name }));
});

/// Rajattu käyttäjä ilman kelvollista valintaa → valitse ensimmäinen
/// sallittu brändi ja lataa uudelleen, jotta jokainen store hakee
/// datansa brändisidottuna (muuten API vastaa 403 tai kaventaa itse).
watch(
  () => [isRestricted.value, options.value] as const,
  ([restricted, opts]) => {
    if (!restricted || opts.length === 0) return;
    const current = switcher.selectedCode;
    if (current && opts.some((b) => b.code === current)) return;
    switcher.select(opts[0]!.code);
    window.location.reload();
  },
);

const value = computed({
  get: () => switcher.selectedCode ?? "",
  set: (v: string) => {
    switcher.select(v.length === 0 ? null : v);
    // Force a hard reload so every cached store re-fetches with the
    // new brand scope. Cheaper than wiring a refresh event into
    // every store; the user changes brand context infrequently.
    window.location.reload();
  },
});

onMounted(async () => {
  await userBrands.fetchMine();
  // Admin / rajaamaton käyttäjä: switcher tarvitsee FULL-listan
  // riippumatta nykyisestä brand-kontekstista — unscoped-versio.
  // Rajatulle käyttäjälle kutsu vastaisi 403 (ei brändivalintaa).
  if (!isRestricted.value && brands.allBrands.length === 0) {
    void brands.fetchAllUnscoped();
  }
});
</script>

<template>
  <label v-if="options.length > 0" class="brand-switcher">
    <span class="visually-hidden">Brändi</span>
    <select v-model="value" :title="'Brändi-rajaus'">
      <option v-if="!isRestricted" value="">Kaikki brändit</option>
      <option
        v-for="b in options"
        :key="b.id"
        :value="b.code"
      >
        {{ b.code }}
      </option>
    </select>
  </label>
</template>

<style scoped>
.brand-switcher select {
  background: transparent;
  color: inherit;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
  cursor: pointer;
}
.brand-switcher select:hover { border-color: #9ca3af; }

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
